import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";

import HeaderContainer from "../templates/armon/panels/HeaderContainer";

import WeekScheduleContainer from "../templates/armon/panels/WeekScheduleContainer";
import ShabbatAndCourseScheduleContainer from "../templates/armon/panels/ShabbatAndCourseScheduleContainer";
import HolidayScheduleContainer from "../templates/armon/panels/HolidayScheduleContainer";
import WeeklyCourseContainer from "../templates/armon/panels/WeeklyCourseContainer";
import ZmanimContainer from "../templates/armon/panels/ZmanimContainer";
import AdContainer from "../templates/armon/panels/AdContainer";
import RdvContainer from "../templates/armon/panels/RdvContainer";
import QrCodeContainer from "../templates/armon/panels/QrCodeContainer";
import CommunityInfoContainer from "../templates/armon/panels/CommunityInfoContainer";
import KehilatiContainer from "../templates/armon/panels/KehilatiContainer";

import { formatTime, formatShiurTime } from "../../utils/localeUtils";
import { normalizeSlotBlocks, slotHasVisibleBlocks } from "../../utils/layoutSlotUtils";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(20px); }
`;

const AnimatedContent = styled.div`
  animation: ${({ $isEntering }) =>
    $isEntering ? css`${fadeIn} 0.5s ease-out forwards` : css`${fadeOut} 0.5s ease-out forwards`};
`;

const Container = styled.div`
  background: url("/images/Armon/fond.png");
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  width: 100%;
  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
`;

const BasePanel = styled.div`
  padding: 20px 40px;
  display: flex;
  width: 432px;
  flex-direction: column;
  font-size: clamp(12px, 1vw, 16px);
  height: 100%;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 380px;
    height: auto;
    margin: 0;
    padding: 20px;
    background-size: 380px auto;
    background-position: top center;
  }
`;

const SlotPanel = styled(BasePanel)`
  padding: ${({ $bg, $noPadding }) => ($noPadding || $bg === "none" ? 0 : "20px 40px")};
  /* Center this panel inside its grid cell (desktop). */
  justify-self: center;
  align-self: center;
  height: ${({ $bg }) => {
    // Mêmes hauteurs que les panels ArmonAlef pour que les bords/paddings
    // tombent au même endroit visuellement.
    switch ($bg) {
      case "fin":
        return "566px";
      case "long":
        return "876px";
      case "large":
        return "285px";
      case "none":
      default:
        return "100%";
    }
  }};
  margin-bottom: auto;
  margin-top: ${({ $bg }) => ($bg === "large" ? "auto" : "0")};
  background: ${({ $bg }) => {
    switch ($bg) {
      case "none":
        return "none";
      case "fin":
        return "url('/images/Armon/ornement fin.png') no-repeat center top";
      case "large":
        return "url('/images/Armon/ornement large.png') no-repeat center top";
      case "long":
      default:
        return "url('/images/Armon/ornement long.png') no-repeat center top";
    }
  }};
  background-size: cover;

  @media (max-width: 768px) {
    margin-top: 0;
    width: 95vw;
    min-width: 95vw;
    background-size: contain;
    padding: ${({ $noPadding }) => ($noPadding ? 0 : "10px 30px")};
    height: ${({ $bg }) => {
      // Mêmes hauteurs que les panels ArmonAlef pour que les bords/paddings
      // tombent au même endroit visuellement.
      switch ($bg) {
        case "fin":
          return "125vw";
        case "long":
          return "192vw";
        case "large":
          return "61vw";
        case "none":
        default:
          return "fit-content";
      }
    }};
  }
`;

const SlotStack = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  // overflow: auto;
`;

const SlideViewport = styled.div`
  position: relative;
  flex: 1;
  height: calc(100vh - 150px);
  overflow: hidden;
  @media (max-width: 768px) {
    height: auto;
  }
`;

const Separator = styled.div`
  height: 1px;
  background-color: rgba(45, 54, 83, 0.2);
  width: 90%;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const SlideColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  position: relative;
`;

/** Conteneur relatif : la grille prend toute la largeur ; Kehilati desktop est en absolu dans la marge. */
const SlideMainRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  width: 100%;
  padding: 1rem;
  box-sizing: border-box;
`;

const ContentColumn = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

/** Dock vertical : flottant dans la marge droite (ne réserve pas de place dans le flux) */
const KehilatiDockWrap = styled.div`
  position: absolute;
  right: 4px;
  top: 52%;
  transform: translateY(-50%);
  max-width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: auto;
`;

/** Bandeau Kehilati sous les infos communauté (mobile uniquement) */
const KehilatiMobileWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
  padding: 0 4px;
  box-sizing: border-box;
`;

const SlideLayer = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${({ $opacity }) => $opacity};
  /* Ne pas animer l’opacité hors crossfade : sinon à la fin du swap la couche repasse 0→1
     et on voit un second fondu (clignotement). */
  transition: ${({ $useOpacityTransition }) =>
    $useOpacityTransition ? "opacity 0.6s ease-in-out" : "none"};

  @media (max-width: 768px) {
    position: relative;
    opacity: 1;
    transition: none;
  }
`;

const SlotGridBase = styled.div`
  display: grid;
  row-gap: 10px;
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  min-height: fit-content;
  height: auto;
  width: 100%;
  align-items: stretch;
  /* Center each grid item inside its own grid area (desktop). */
  justify-items: center;
  align-items: center;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    height: auto;
    margin: 0;
    padding: 0;
    gap: 20px;
    align-items: center;
  }
`;

const DESIGN_DEFS = {
  "3col": {
    colCount: 3,
    gridTemplateRows: "67fr 33fr",
    slotDefs: [
      { id: "slot-1", colStart: 1, colEnd: 2, rowStart: 1, rowEnd: 2, bg: "fin" },
      { id: "slot-2", colStart: 1, colEnd: 2, rowStart: 2, rowEnd: 3, bg: "large" },
      { id: "slot-3", colStart: 2, colEnd: 3, rowStart: 1, rowEnd: 3, bg: "long" },
      { id: "slot-4", colStart: 3, colEnd: 4, rowStart: 1, rowEnd: 3, bg: "long" },
    ],
  },
  "4col": {
    colCount: 4,
    gridTemplateRows: "67fr 33fr",
    slotDefs: [
      // Alignement sur ArmonAlef:
      // - col1: top (fin) + bottom (large)
      // - col2/col3: full height (long)
      // - col4: top (ads encart pub) + bottom (QR large)
      { id: "slot-1", colStart: 1, colEnd: 2, rowStart: 1, rowEnd: 2, bg: "fin" },
      { id: "slot-2", colStart: 1, colEnd: 2, rowStart: 2, rowEnd: 3, bg: "large" },
      { id: "slot-3", colStart: 2, colEnd: 3, rowStart: 1, rowEnd: 3, bg: "long" },
      { id: "slot-4", colStart: 3, colEnd: 4, rowStart: 1, rowEnd: 3, bg: "long" },
      { id: "slot-5", colStart: 4, colEnd: 5, rowStart: 1, rowEnd: 2, bg: "none" },
      { id: "slot-6", colStart: 4, colEnd: 5, rowStart: 2, rowEnd: 3, bg: "large" },
    ],
  },
};

const DEFAULT_SLIDES = [
  {
    design: "4col",
    delaySec: 5,
    slots: {},
  },
];

const SlotBlock = ({ assignment, data, showShabbatInfo, t, slotOrnament }) => {
  const { schedule, shiur, zmanim, adImages, community, nextEvents, parasha, fontSizes, language } = data;

  if (!assignment) return null;

  const blockType = assignment.blockType;

  // Slot-level scroll option: only some block types support it.
  const autoScroll = Boolean(assignment.autoScroll);

  if (blockType === "WEEK_SECTION") {
    const sectionSchedule = (schedule || []).filter(
      (s) => s.category === "Week" && String(s.sectionId) === String(assignment.sectionId),
    );
    if (!sectionSchedule.length) return null;
    return (
      <WeekScheduleContainer weekSchedule={sectionSchedule} fontSize={fontSizes?.Week} AnimatedContent={AnimatedContent} />
    );
  }

  if (blockType === "SHABBAT_SECTION") {
    const sectionSchedule = (schedule || []).filter(
      (s) => s.category === "Shabbat" && String(s.sectionId) === String(assignment.sectionId),
    );
    if (!sectionSchedule.length) return null;

    const shiurWithoutSaturdayCourses = {
      ...(shiur || {}),
      saturday: [],
    };

    return (
      <ShabbatAndCourseScheduleContainer
        isTransitioning={undefined}
        parasha={parasha}
        showShabbatInfo={showShabbatInfo}
        shabatSchedule={sectionSchedule}
        shiur={shiurWithoutSaturdayCourses}
        formatTime={(dateString) => formatTime(dateString, language)}
        formatShiurTime={formatShiurTime}
        AnimatedContent={AnimatedContent}
        fontSize={fontSizes?.Shabbat}
        community={community}
        language={language}
      />
    );
  }

  if (blockType === "CHAGUIM_SECTION") {
    const sectionSchedule = (schedule || []).filter(
      (s) =>
        String(s.sectionId) === String(assignment.sectionId) &&
        s.category !== "Week" &&
        s.category !== "Shabbat",
    );
    if (!sectionSchedule.length) return null;

    return (
      <HolidayScheduleContainer
        holidaySchedule={sectionSchedule}
        isTransitioning={undefined}
        AnimatedContent={AnimatedContent}
        fontSize={fontSizes?.Holiday}
      />
    );
  }

  if (blockType === "COURSES_WEEKLY") {
    return (
      <WeeklyCourseContainer
        shiur={shiur}
        fontSize={fontSizes?.Week}
        autoScroll={autoScroll}
      />
    );
  }

  if (blockType === "ZMANIM") {
    return (
      <ZmanimContainer
        zmanim={zmanim}
        fontSize={fontSizes?.Zmanim}
        autoScroll={autoScroll}
        slotOrnament={slotOrnament}
      />
    );
  }

  if (blockType === "ANNOUNCEMENTS_ADS") {
    return <AdContainer adImages={adImages} />;
  }

  if (blockType === "QR_CODE") {
    return <QrCodeContainer qrCode={community?.qrCode} qrCodeText={community?.qrCodeText} />;
  }

  if (blockType === "NEXT_RDV") {
    return <RdvContainer nextEvents={nextEvents} autoScroll={autoScroll} />;
  }

  // Unknown block types: do not render.
  return null;
};

const Slot = ({ slotDef, slotAssignments, data, t }) => {
  const blocks = normalizeSlotBlocks(slotAssignments).filter(Boolean);
  if (!blocks.length) return null;

  const noPadding = blocks.every((b) => b?.blockType === "ANNOUNCEMENTS_ADS");

  return (
    <SlotPanel
      $bg={slotDef.bg}
      $noPadding={noPadding}
      style={{
        gridColumn: `${slotDef.colStart} / ${slotDef.colEnd}`,
        gridRow: `${slotDef.rowStart} / ${slotDef.rowEnd}`,
      }}
    >
      <SlotStack>
        {(() => {
          let shabbatInfoAlreadyShown = false;
          return blocks.map((assignment) => {
            const showShabbatInfoForThisBlock =
              assignment?.blockType === "SHABBAT_SECTION" ? !shabbatInfoAlreadyShown : undefined;
            if (assignment?.blockType === "SHABBAT_SECTION") shabbatInfoAlreadyShown = true;

            return (
              <SlotBlock
                key={assignment.id}
                assignment={assignment}
                data={data}
                showShabbatInfo={showShabbatInfoForThisBlock}
                t={t}
                slotOrnament={slotDef.bg}
              />
            );
          });
        })()}
      </SlotStack>
    </SlotPanel>
  );
};

/**
 * LayoutRenderer:
 * - renders a header + a slot grid
 * - cycles slides based on `delaySec` stored in layoutConfig
 */
const CROSSFADE_MS = 600;

const LayoutRenderer = ({ layoutConfig, data }) => {
  const { t } = useTranslation();
  const slides = layoutConfig?.slides || [];
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [nextSlideIndex, setNextSlideIndex] = useState(null);
  const crossfadeEndTimeoutRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:768px)");

  const slidesSafe = slides && slides.length ? slides : DEFAULT_SLIDES;

  const slidePreparedData = data;

  useEffect(() => {
    if (slidesSafe.length <= 1) return;

    const currentSlide = slidesSafe[currentSlideIndex] || {};
    const delaySec = Number(currentSlide.delaySec) || 5;
    const delayTimeout = setTimeout(() => {
      const newIndex = (currentSlideIndex + 1) % slidesSafe.length;
      setNextSlideIndex(newIndex);
      // Aligné sur SlideLayer transition (~600ms)
      crossfadeEndTimeoutRef.current = setTimeout(() => {
        crossfadeEndTimeoutRef.current = null;
        setCurrentSlideIndex(newIndex);
        setNextSlideIndex(null);
      }, CROSSFADE_MS);
    }, delaySec * 1000);

    return () => {
      clearTimeout(delayTimeout);
      if (crossfadeEndTimeoutRef.current) {
        clearTimeout(crossfadeEndTimeoutRef.current);
        crossfadeEndTimeoutRef.current = null;
      }
    };
  }, [currentSlideIndex, slidesSafe]);

  const inCrossfade = nextSlideIndex !== null;

  const renderSlide = (slideIdx) => {
    const slide = slidesSafe[slideIdx] || {};
    const designKey = slide.design || "4col";
    const design = DESIGN_DEFS[designKey] || DESIGN_DEFS["4col"];

    return (
      <SlideColumn>
        <SlideMainRow>
          <ContentColumn>
            <SlotGridBase
              style={{
                gridTemplateColumns: `repeat(${design.colCount}, 1fr)`,
                gridTemplateRows: design.gridTemplateRows,
              }}
            >
              {design.slotDefs.map((slotDef) => {
                const raw = slide.slots?.[slotDef.id];
                if (!slotHasVisibleBlocks(raw)) return null;
                return (
                  <Slot
                    key={`${designKey}-${slotDef.id}`}
                    slotDef={slotDef}
                    slotAssignments={raw}
                    data={slidePreparedData}
                    t={t}
                  />
                );
              })}
            </SlotGridBase>
            <CommunityInfoContainer community={slidePreparedData.community} t={t} />
            <Separator />
            {isMobile && (
              <KehilatiMobileWrap>
                <KehilatiContainer variant="default" keepHorizontalOnMobile />
              </KehilatiMobileWrap>
            )}
          </ContentColumn>
          {!isMobile && (
            <KehilatiDockWrap>
              <KehilatiContainer variant="layoutDock" />
            </KehilatiDockWrap>
          )}
        </SlideMainRow>
      </SlideColumn>
    );
  };

  // Slide show: overlapping layers for smooth transition.
  const currentOpacity = nextSlideIndex === null ? 1 : 0;
  const nextOpacity = nextSlideIndex === null ? 0 : 1;

  return (
    <>
      <Container>
        <HeaderContainer
          zmanim={data.zmanim}
          logo={data.logo}
          community={data.community}
          sponsor={data.sponsor}
          omer={data.omer}
        />
        <SlideViewport>
          <SlideLayer
            $opacity={currentOpacity}
            $useOpacityTransition={inCrossfade}
          >
            {renderSlide(currentSlideIndex)}
          </SlideLayer>
          {nextSlideIndex !== null && (
            <SlideLayer $opacity={nextOpacity} $useOpacityTransition>
              {renderSlide(nextSlideIndex)}
            </SlideLayer>
          )}
        </SlideViewport>
      </Container>
    </>
  );
};

export default LayoutRenderer;
